import {
  Box,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { CandlestickController, CandlestickElement } from "chartjs-chart-financial";
import { Bar, Chart, Line, Pie } from "react-chartjs-2";
import {
  InsightsChartSkeleton,
  InsightsEmptyState,
  InsightsErrorState,
} from "../../components/Insights/InsightsAsyncState";
import { insightsChartPlotSurfaceSx, pricingReportChartTitleSx } from "./constants";
import { PRICING_REPORT_SCREEN_CHART_PLOT_HEIGHT_PX } from "./pricingReportPrintDimensions";

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineController,
  LineElement,
  BarElement,
  CandlestickController,
  CandlestickElement,
  Title,
  Tooltip,
  Legend
);

function dataPointHasValue(value) {
  if (value == null) return false;
  if (typeof value === "object") {
    if (typeof value.y === "number" && Number.isFinite(value.y)) return true;
    return [value.o, value.h, value.l, value.c].some(
      (point) => typeof point === "number" && Number.isFinite(point)
    );
  }
  return typeof value === "number" && Number.isFinite(value);
}

export const pricingLineChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: { display: true, position: "bottom" },
    tooltip: {
      callbacks: {
        label: (context) => {
          const value = typeof context.parsed?.y === "number" ? context.parsed.y.toFixed(2) : "—";
          return `${context.dataset.label}: $${value}`;
        },
      },
    },
  },
  scales: {
    y: {
      title: { display: true, text: "Avg unit price (CAD)" },
    },
  },
};

export const pricingCandlestickChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: { display: true, position: "bottom" },
    tooltip: {
      callbacks: {
        label: (context) => {
          if (context.dataset.type === "line") {
            const value = typeof context.parsed?.y === "number" ? context.parsed.y.toFixed(2) : "—";
            return `${context.dataset.label}: $${value}`;
          }
          const point = context.raw;
          if (!point || typeof point !== "object") return `${context.dataset.label}: —`;
          const avg =
            typeof point.avg === "number"
              ? point.avg.toFixed(2)
              : typeof point.c === "number" && typeof point.o === "number"
                ? ((point.o + point.c) / 2).toFixed(2)
                : "—";
          const high = typeof point.h === "number" ? point.h.toFixed(2) : "—";
          const low = typeof point.l === "number" ? point.l.toFixed(2) : "—";
          return [`${context.dataset.label}`, `Avg: $${avg}`, `High: $${high}`, `Low: $${low}`];
        },
      },
    },
  },
  datasets: {
    candlestick: {
      barPercentage: 0.55,
      categoryPercentage: 0.75,
      maxBarThickness: 32,
    },
    line: {
      borderWidth: 2,
    },
  },
  scales: {
    x: {
      type: "category",
      offset: true,
    },
    y: {
      title: { display: true, text: "Avg unit price (CAD)" },
    },
  },
};

export function chartHasSeriesData(chart) {
  return (chart?.datasets || []).some((dataset) =>
    (dataset?.data || []).some((value) => dataPointHasValue(value))
  );
}

export default function ChartBlock({
  title,
  subtitle,
  chart,
  chartOptions = pricingLineChartOptions,
  chartType = "line",
  chartRef,
  isLoading = false,
  error = null,
  framed = true,
  hasData: hasDataProp,
  emptyMessage = "No chart data available for the current filters.",
  onSeriesClick,
  sx,
}) {
  const hasData = typeof hasDataProp === "boolean" ? hasDataProp : chartHasSeriesData(chart);
  const ChartComponent =
    chartType === "bar"
      ? Bar
      : chartType === "candlestick"
        ? Chart
        : chartType === "pie"
          ? Pie
          : Line;
  // chartjs-chart-financial mixed-chart demo: top-level type is candlestick; the
  // line dataset sets type: "line" and uses { x, y } points (see docs/index.js).
  const resolvedChartType = chartType === "candlestick" ? "candlestick" : undefined;
  const resolvedChartOptions =
    chartType === "candlestick" ? pricingCandlestickChartOptions : chartOptions;

  return (
    <Box
      sx={
        framed
          ? { p: 2, minWidth: 0, width: "100%", maxWidth: "100%", overflowX: "hidden", ...insightsChartPlotSurfaceSx, ...sx }
          : { minWidth: 0, width: "100%", maxWidth: "100%", ...sx }
      }
    >
      {title ? (
        <Typography sx={pricingReportChartTitleSx}>
          {title}
        </Typography>
      ) : null}
      {subtitle ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: title ? 0.5 : 0, mb: 1.5 }}>
          {subtitle}
        </Typography>
      ) : null}

      {error ? (
        <InsightsErrorState
          message={error}
          fallbackMessage="Something went wrong while loading chart data."
        />
      ) : null}
      {isLoading ? <InsightsChartSkeleton sx={{ mt: title || subtitle ? 0 : undefined }} /> : null}
      {!isLoading && !error && !hasData ? <InsightsEmptyState message={emptyMessage} /> : null}
      {!isLoading && !error && hasData ? (
        <Box
          data-pricing-chart-plot
          sx={{
            height: PRICING_REPORT_SCREEN_CHART_PLOT_HEIGHT_PX,
            minWidth: 0,
            width: "100%",
            maxWidth: "100%",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            px: chartType === "pie" ? 2 : 0,
            py: chartType === "pie" ? 1 : 0,
          }}
        >
          <ChartComponent
            ref={chartRef}
            type={resolvedChartType}
            data={chart}
            options={resolvedChartOptions}
            onClick={(_, elements, chartInstance) => {
              if (typeof onSeriesClick !== "function" || !elements?.length) return;
              const datasetIndex = elements[0]?.datasetIndex;
              if (datasetIndex == null) return;
              const dataset = chartInstance?.data?.datasets?.[datasetIndex];
              if (!dataset?.entityValue) return;
              onSeriesClick(dataset.entityValue);
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
}

ChartBlock.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  chart: PropTypes.object,
  chartOptions: PropTypes.object,
  chartType: PropTypes.oneOf(["line", "bar", "candlestick", "pie"]),
  chartRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  isLoading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  framed: PropTypes.bool,
  hasData: PropTypes.bool,
  emptyMessage: PropTypes.string,
  onSeriesClick: PropTypes.func,
  sx: PropTypes.object,
};
